'use client';

/**
 * AnalyticsService - Centralized Analytics Tracking Service
 *
 *
 * @example
 * ```tsx
 * // Initialize once in app layout
 * const service = AnalyticsService.getInstance();
 * service.init({ sendEvent: sendGTMEvent });
 * ```
 */

import type { AnalyticsEvent } from '../composables/Analytics/types';
import { ANALYTICS_EVENT_TRIGGERS, type AnalyticsEventTrigger } from '../utils/analytics/constants';
import { createDevLogger } from '../utils';

const logger = createDevLogger({ namespace: 'AnalyticsService' });

interface AnalyticsServiceOptions {
  /**
   * Function to send events to GTM
   * Should be sendGTMEvent from '@next/third-parties/google' in Next.js apps
   */
  sendEvent: (event: AnalyticsEvent) => void;
  /**
   * IntersectionObserver threshold (0-1)
   * @default 0.5
   */
  impressionThreshold?: number;
}

export interface InitConfig {
  /**
   * Whether to automatically start impression observers on init.
   * Set to false to defer observer startup (e.g., until after page_view fires).
   * @default true
   */
  autoStartObservers?: boolean;
}

interface GAConfig {
  [ANALYTICS_EVENT_TRIGGERS.IMPRESSION]?: AnalyticsEvent;
  [ANALYTICS_EVENT_TRIGGERS.CLICK]?: AnalyticsEvent;
  [ANALYTICS_EVENT_TRIGGERS.MODAL_OPEN]?: AnalyticsEvent;
  [ANALYTICS_EVENT_TRIGGERS.MODAL_CLOSE]?: AnalyticsEvent;
  [ANALYTICS_EVENT_TRIGGERS.MODAL_NAVIGATE]?: AnalyticsEvent;
  [ANALYTICS_EVENT_TRIGGERS.CUSTOM_EVENT]?: AnalyticsEvent;
  [ANALYTICS_EVENT_TRIGGERS.HOVER_EVENT]?: AnalyticsEvent;
  [ANALYTICS_EVENT_TRIGGERS.ACCORDION_CLICK]?: AnalyticsEvent;
  // Future: focus_event?, etc.
}

/**
 * Singleton service for managing analytics tracking via data attributes
 *
 * Features:
 * - Event delegation for click tracking (single listener)
 * - IntersectionObserver for impression tracking
 * - Parses data-ga-config JSON attributes
 * - Works with Server Components (no 'use client' needed)
 * - Integrates with existing ConsentContext via GTM
 */
export class AnalyticsService {
  private static instance: AnalyticsService | null = null;
  private sendEvent: ((event: AnalyticsEvent) => void) | null = null;
  private impressionObserver: IntersectionObserver | null = null;
  private isInitialized = false;
  private impressionThreshold = 0.5;
  private firedOnceByElement: WeakMap<HTMLElement, Set<string>> = new WeakMap();
  private configCache: WeakMap<HTMLElement, GAConfig> = new WeakMap();
  private pageContext: { pageTemplate?: string; locale?: string } = {};

  /**
   * Private constructor for singleton pattern
   */
  private constructor() {}

  /**
   * Get singleton instance
   */
  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  /**
   * Initialize the analytics service
   * Should be called once in app layout
   */
  public init(options: AnalyticsServiceOptions, config?: InitConfig): void {
    if (this.isInitialized) {
      logger.warn('AnalyticsService already initialized');
      return;
    }

    if (typeof window === 'undefined') {
      logger.warn('AnalyticsService can only be initialized in browser');
      return;
    }

    this.sendEvent = options.sendEvent;
    this.impressionThreshold = options.impressionThreshold ?? 0.5;

    // Set up click event delegation
    document.addEventListener('click', this.handleClick, true);

    // Set up mouseenter event delegation for custom hover events (tooltips, etc.)
    document.addEventListener('mouseenter', this.handleMouseEnter, true);

    const autoStart = config?.autoStartObservers ?? true;
    if (autoStart) {
      this.observeImpressions();
    }

    this.isInitialized = true;
    logger.info('AnalyticsService initialized', { autoStartObservers: autoStart });
  }

  /**
   * Start (or restart) impression observers.
   */
  public startObservers(): void {
    if (!this.isInitialized) return;

    if (this.impressionObserver) {
      this.impressionObserver.disconnect();
    }

    this.observeImpressions();
    logger.info('Impression observers started');
  }

  /**
   * Clean up event listeners and observers
   */
  public cleanup(): void {
    if (typeof window === 'undefined') return;

    document.removeEventListener('click', this.handleClick, true);
    document.removeEventListener('mouseenter', this.handleMouseEnter, true);

    if (this.impressionObserver) {
      this.impressionObserver.disconnect();
      this.impressionObserver = null;
    }

    // Clear references to prevent memory leaks
    this.sendEvent = null;
    this.configCache = new WeakMap();
    this.firedOnceByElement = new WeakMap();

    this.isInitialized = false;
    logger.info('AnalyticsService cleaned up');
  }

  /**
   * Refresh impression observers (e.g., after route change in SPA)
   * Call this after dynamic content is loaded
   */
  public refreshObservers(): void {
    if (!this.isInitialized) return;

    // Disconnect existing observer
    if (this.impressionObserver) {
      this.impressionObserver.disconnect();
    }

    // Re-scan and observe new elements
    this.observeImpressions();
    logger.info('Impression observers refreshed');
  }

  /**
   * Set page context for automatic injection into all events
   * Called by PageContextProvider to provide normalized page template and locale
   *
   * @param pageTemplate - Normalized page identifier (e.g., 'newsroom', 'safety')
   * @param locale - Current locale (e.g., 'en', 'zh-hant')
   *
   * @example
   * ```tsx
   * // In PageContextProvider
   * useEffect(() => {
   *   analyticsService.setPageContext(pageTemplate, locale);
   * }, [pageTemplate, locale]);
   * ```
   */
  public setPageContext(pageTemplate: string, locale: string): void {
    if (pageTemplate === 'corp-home' || pageTemplate === 'career-home') {
      pageTemplate = 'home';
    }
    this.pageContext = { pageTemplate, locale };
    logger.debug('Page context updated:', this.pageContext);
  }

  /**
   * Programmatically track an event by merging element-configured payload and runtime overrides.
   *
   * @param element - HTML element with data-ga-config attribute
   * @param trigger - Event trigger type (click, impression, modalOpen, etc.)
   * @param overrides - Optional runtime data to merge with element config
   * @param options - Optional settings (once: fire only once per element+trigger)
   *
   * @example
   * // Track carousel navigation with dynamic index
   * analyticsService.trackFromElement(ref.current, EVENT_TRIGGERS.CUSTOM_EVENT, { current_index: 3 });
   *
   * @example
   * // Track modal open
   * analyticsService.trackFromElement(modalRef.current, EVENT_TRIGGERS.MODAL_OPEN);
   *
   * @example
   * // Track with once option (deduplicate)
   * analyticsService.trackFromElement(ref.current, EVENT_TRIGGERS.CLICK, {}, { once: true });
   */
  public trackFromElement(
    element: HTMLElement | null | undefined,
    trigger: AnalyticsEventTrigger,
    overrides?: Partial<AnalyticsEvent>,
    options?: { once?: boolean }
  ): void {
    if (!this.isInitialized || !element) return;

    const base = this.getEventConfig(element, trigger);
    if (!base && !overrides) return;

    const merged = { ...(base ?? {}), ...(overrides ?? {}) } as AnalyticsEvent;

    // Optional once-per-element+trigger dedupe
    if (options?.once) {
      const eventName = (merged as unknown as Record<string, unknown>).event;
      const key = `${trigger}:${eventName ?? 'unknown'}`;
      const existing = this.firedOnceByElement.get(element) ?? new Set<string>();
      if (existing.has(key)) return;
      existing.add(key);
      this.firedOnceByElement.set(element, existing);
    }

    this.trackEvent(merged);
  }

  /**
   * Handle click events via delegation
   * Priority: custom_event (more specific) > click (generic)
   */
  private handleClick = (e: MouseEvent): void => {
    const target = e.target;

    // Ensure target is an Element (not Document or Window) before using closest
    if (!(target instanceof Element)) return;

    // Find closest element with data-ga-config
    const clickableElement = target.closest('[data-ga-config]') as HTMLElement | null;

    if (!clickableElement) return;

    // Check for custom_event first (more specific), then fall back to generic click
    const config =
      this.getEventConfig(clickableElement, ANALYTICS_EVENT_TRIGGERS.CUSTOM_EVENT) ||
      this.getEventConfig(clickableElement, ANALYTICS_EVENT_TRIGGERS.CLICK);

    if (config) {
      logger.debug('Element clicked, tracking event:', clickableElement);
      this.trackEvent(config);
    }
  };

  /**
   * Handle mouseenter events via delegation for hover-triggered events
   * (tooltips, popovers, etc.)
   */
  private handleMouseEnter = (e: MouseEvent): void => {
    const target = e.target;

    // Ensure target is an Element (not Document or Window) before using closest
    if (!(target instanceof Element)) return;

    // Find closest element with data-ga-config
    const hoverableElement = target.closest('[data-ga-config]') as HTMLElement | null;

    if (!hoverableElement) return;

    // Check for hover_event (e.g., tooltip_open)
    const config = this.getEventConfig(hoverableElement, ANALYTICS_EVENT_TRIGGERS.HOVER_EVENT);

    if (config) {
      logger.debug('Element hovered, tracking hover event:', hoverableElement);
      this.trackEvent(config);
    }
  };

  /**
   * Set up IntersectionObserver for impression tracking
   */
  private observeImpressions(): void {
    if (typeof window === 'undefined') return;

    // Create observer with configurable threshold
    this.impressionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Track both currently intersecting AND elements that are already visible
          // entry.isIntersecting will be true immediately for already-visible elements
          if (entry.isIntersecting && entry.intersectionRatio >= this.impressionThreshold) {
            const config = this.getEventConfig(
              entry.target as HTMLElement,
              ANALYTICS_EVENT_TRIGGERS.IMPRESSION
            );

            if (config) {
              logger.debug('Element became visible, tracking impression:', entry.target);
              this.trackEvent(config);
              // Unobserve after tracking (fire once)
              this.impressionObserver?.unobserve(entry.target);
            }
          }
        });
      },
      {
        threshold: this.impressionThreshold,
        rootMargin: '0px',
      }
    );

    // Find all elements with impression config and observe them
    const elementsToObserve = this.findElementsWithImpressionConfig();
    elementsToObserve.forEach((el) => {
      this.impressionObserver?.observe(el);
    });

    logger.info(`Observing ${elementsToObserve.length} elements for impressions`);
  }

  /**
   * Find all elements that have impression tracking configured
   */
  private findElementsWithImpressionConfig(): HTMLElement[] {
    const elements: HTMLElement[] = [];
    const allConfigElements = document.querySelectorAll('[data-ga-config]');

    allConfigElements.forEach((el) => {
      const configAttr = el.getAttribute('data-ga-config');
      if (configAttr && configAttr.includes('"impression"')) {
        elements.push(el as HTMLElement);
      }
    });

    return elements;
  }

  /**
   * Get event configuration from data-ga-config attribute
   * with JSON parsing cache for performance
   *
   * @param element - HTML element with data-ga-config
   * @param eventType - Type of event trigger to extract
   * @returns Analytics event config or null if not found/invalid
   */
  private getEventConfig(
    element: HTMLElement,
    eventType: AnalyticsEventTrigger
  ): AnalyticsEvent | null {
    const configAttr = element.getAttribute('data-ga-config');
    if (!configAttr) return null;

    // Check cache first for performance
    let config = this.configCache.get(element);
    if (!config) {
      try {
        const parsed: GAConfig = JSON.parse(configAttr);
        this.configCache.set(element, parsed);
        config = parsed;
      } catch (error) {
        logger.warn('Failed to parse analytics config:', error, element);
        return null;
      }
    }

    return config[eventType] || null;
  }

  /**
   * Clean event payload by converting empty-string values to undefined
   */
  private cleanEventPayload(event: AnalyticsEvent): AnalyticsEvent {
    const cleaned = AnalyticsService.normalizeRecord(event as unknown as Record<string, unknown>);
    return cleaned as unknown as AnalyticsEvent;
  }

  /**
   * Track an analytics event
   * Automatically injects page_template and content_language from page context
   * Sends to GTM via provided sendEvent function
   *
   * @public - Exposed for programmatic event tracking (e.g., modal navigation)
   */
  public trackEvent(event: AnalyticsEvent): void {
    if (!this.sendEvent) {
      logger.warn('sendEvent not configured, cannot track event:', event);
      return;
    }

    // Auto-inject page context (page_template and content_language)
    const eventWithContext = {
      ...event,
      ...(this.pageContext.pageTemplate && { page_template: this.pageContext.pageTemplate }),
      ...(this.pageContext.locale && { content_language: this.pageContext.locale }),
    };

    const cleaned = this.cleanEventPayload(eventWithContext);
    logger.info('📊 Tracking event:', cleaned);
    this.sendEvent(cleaned);
  }
}

/**
 * Export singleton instance for convenience
 */
export const analyticsService = AnalyticsService.getInstance();

// Internal helpers
interface AnyRecord {
  [key: string]: unknown;
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace AnalyticsService {
  /**
   * Convert empty-string values to undefined for cleaner GA payloads
   */
  export function normalizeRecord<T extends AnyRecord>(input: T): T {
    const output: AnyRecord = {};
    Object.entries(input).forEach(([key, value]) => {
      output[key] = value === '' ? undefined : value;
    });
    return output as T;
  }
}
